import React, {
  KeyboardEvent,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Alert } from '@mui/material';
import classNames from 'classnames';
import { Button } from 'Components/Core/Button';
import Checkbox from 'Components/Core/Checkbox/Checkbox';
import ActionButtonDropdown from 'Components/Core/DataTable/Components/DataTableActionButtonsDropdown';
import DataTableHeader from 'Components/Core/DataTable/Components/DataTableHeader';
import { DataTableColumnProps, DataTableProps } from 'Components/Core/DataTable/types';
import { HbtIcon } from 'Components/Core/HbtIcon';
import { HbtIconSize } from 'Components/Core/HbtIcon/types';
import useEnterOrSpaceKey, {
  handleMultipleElementClicks
} from 'Components/Hooks/UseEnterOrSpaceKey';
import i18n from 'i18next';
import { debounce } from 'lodash';
import { useHotkeys } from 'react-hotkeys-hook';
import { ButtonSize, ButtonVariant } from '../Button/types';
import { MouseOrKeyboardEvent } from '../Modal/types';
import styles from './styles.module.scss';

/**
 *
 * @param data - Data to be displayed in the table
 * @param actionButtons - Action buttons to display for each row
 * @param columnActionButtons - Whether to display action buttons in the first column
 * @param columnResize - Whether the columns are resizeable
 * @param multipleRowSubmission - Whether multiple rows can be submitted
 * @param stickyFirstColumn - Whether the first column is sticky
 * @param sortedColumn - The column that is currently sorted
 * @param sortAscending - Whether the sort is ascending
 * @param submitButtonLabel - The label for the submit button
 * @param tableCaption - The caption for the table
 * @param onRowClick - Callback for when a row is clicked
 * @param onSort - Callback for when a column is sorted
 * @param onSubmit - Callback for when the submit button is clicked
 * @param children - The columns of the table
 * @param uniqueKey - A unique key to identify each row in the table
 * @param isLoading - Whether the table data is currently loading
 * @param isDeleteButtonLoading - Whether the delete button is currently loading
 * @param isSubmitButtonLoading - Whether the submit button is currently loading
 * @param clearSelectionRows - Whether the row selection should be cleared
 * @param deleteItem - Function to delete a selected item
 * @param deleteButtonLabel - The label for the delete button
 * @param footer - Content for the table's footer
 * @param emptyTableMessage - Message to display when the table has no data
 * @param errorType - Type of error to display, if any
 * @param onColumnsReady - Callback when the columns are ready
 * @param onDelete - Callback for when an item is deleted
 * @param onRowSelectionChange - Callback when the row selection changes
 * @param onRowSelectionCleared - Callback when the row selection is cleared
 * @param tableErrorMessage - Error message to display for the table
 * @param submitButtonStyles - Custom styles for the submit button(s)
 */

/*
  This component is used to display a table of data. 
  It supports sorting, column resizing, and row selection.
  It also supports submitting multiple rows of data with checkboxes displayed on the first column.  
 */

const DataTable = <T extends object>({
  data,
  uniqueKey,
  isLoading,
  isDeleteButtonLoading = false,
  isSubmitButtonLoading = false,
  clearSelectionRows = false,
  columnActionButtons = false,
  columnResize = false,
  multipleRowSubmission = false,
  stickyFirstColumn = false,
  sortedColumn,
  deleteItem,
  deleteButtonLabel,
  actionButtons,
  footer,
  sortAscending,
  submitButtonLabel = i18n.t('DefaultSubmission-ModalSubmitButton') ?? 'Submit',
  tableCaption = 'HBT Data Table',
  emptyTableMessage,
  errorType = null,
  onColumnsReady,
  onDelete,
  onRowClick,
  onSort,
  onSubmit,
  children,
  tableErrorMessage,
  submitButtonStyles,
  onRowSelectionChange,
  onRowSelectionCleared
}: DataTableProps<T>) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const shadowOverlayRef = useRef<HTMLDivElement>(null);
  const overlayLeftPosRef = useRef<number | null>(null);
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const [isHeaderCheckboxIndeterminate, setIsHeaderCheckboxIndeterminate] = useState(false);
  const [focusedCell, setFocusedCell] = useState<{ row: number; col: number } | null>(null);
  const [showRowActions, setShowRowActions] = useState(false);
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | undefined>(undefined);
  const [openActionRowIndex, setOpenActionRowIndex] = useState<number | null>(null);

  const memoizedChildren = useMemo(() => React.Children.toArray(children), [children]);

  const columns = useMemo(
    () => memoizedChildren as ReactElement<DataTableColumnProps<T>>[],
    [memoizedChildren]
  );

  const columnSettings = useMemo(
    () =>
      columns.map((column) => ({
        key: column.props.name as keyof T,
        displayName: column.props.displayName,
        width: column.props.width ?? 'auto',
        sortable: column.props.sortable ?? true,
        filterable: column.props.filterable ?? true,
        headerAlignment: column.props.headerAlignment ?? 'start',
        rowAlignment: column.props.rowAlignment ?? 'center',
        leadingIcon: column.props.leadingIcon,
        trailingIcon: column.props.trailingIcon,
        sortIconPosition: column.props.sortIconPosition ?? 'trailing',
        render: column.props.children
      })),
    [columns]
  );

  const debouncedOnColumnsReady = useMemo(
    () => debounce((cols) => onColumnsReady && onColumnsReady(cols), 300),
    [onColumnsReady]
  );

  useEffect(() => {
    debouncedOnColumnsReady(columns);
  }, [columns, debouncedOnColumnsReady]);

  useEffect(() => {
    if (clearSelectionRows) {
      setSelectedRows(new Set());
      if (onRowSelectionCleared) {
        onRowSelectionCleared();
      }
    }
  }, [clearSelectionRows, onRowSelectionCleared]);

  useEffect(() => {
    const selectedCount = selectedRows.size;
    if (selectedCount === 0) {
      setIsHeaderCheckboxChecked(false);
      setIsHeaderCheckboxIndeterminate(false);
    } else if (selectedCount === data.length) {
      setIsHeaderCheckboxChecked(true);
      setIsHeaderCheckboxIndeterminate(false);
    } else {
      setIsHeaderCheckboxChecked(false);
      setIsHeaderCheckboxIndeterminate(true);
    }
  }, [selectedRows, data.length]);

  const handleCheckboxChange = useCallback(
    (item: T) => {
      const rowId = item[uniqueKey] as unknown as string;
      setSelectedRows((prevSelectedRows) => {
        const newSelectedRows = new Set(prevSelectedRows);
        if (newSelectedRows.has(rowId)) {
          newSelectedRows.delete(rowId);
        } else {
          newSelectedRows.add(rowId);
        }
        const selectedData = Array.from(newSelectedRows).map((id) =>
          data.find((item) => item[uniqueKey] === id)
        );
        if (onRowSelectionChange) onRowSelectionChange(selectedData as T[]);
        return newSelectedRows;
      });
    },
    [onRowSelectionChange, data]
  );

  const handleHeaderCheckboxChange = useCallback(() => {
    setSelectedRows((prevSelectedRows) => {
      const allSelected = prevSelectedRows.size === data.length;

      const newSelectedRows: Set<string> = allSelected
        ? new Set()
        : new Set(data.map((item: T) => item[uniqueKey] as unknown as string));

      const selectedData = Array.from(newSelectedRows).map((id) =>
        data.find((item) => item[uniqueKey] === id)
      );

      if (onRowSelectionChange) {
        onRowSelectionChange(selectedData as T[]);
      }
      return newSelectedRows;
    });
  }, [data, onRowSelectionChange]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      if (!columnResize) return;

      const startX = e.pageX;
      const startWidth = tableRef.current!.querySelectorAll('th')[index].clientWidth;

      const cells = tableRef.current!.querySelectorAll(
        `th:nth-child(${index + 1}), td:nth-child(${index + 1})`
      );
      cells.forEach((cell) => cell.classList.add(styles.resizing));

      const onMouseMove = (e: MouseEvent) => {
        const newWidth = startWidth + (e.pageX - startX);
        tableRef.current!.querySelectorAll('th')[index].style.width = `${newWidth}px`;
        tableRef.current!.querySelectorAll(`td:nth-child(${index + 1})`).forEach((td) => {
          (td as HTMLElement).style.width = `${newWidth}px`;
        });
      };

      const onMouseUp = () => {
        cells.forEach((cell) => cell.classList.remove(styles.resizing));
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [columnResize]
  );

  const handleRowClick = useCallback(
    (rowItem: T) => {
      const rowId = rowItem[uniqueKey] as T[keyof T];

      if (multipleRowSubmission) {
        handleCheckboxChange(rowItem);
      }
      if (onRowClick) {
        onRowClick(rowId);
      }
    },
    [multipleRowSubmission, handleCheckboxChange, onRowClick, uniqueKey]
  );

  const handleDelete = useCallback(
    (e?: MouseOrKeyboardEvent) => {
      if (onDelete) onDelete(e);
    },
    [onDelete]
  );

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      const selectedData = Array.from(selectedRows).map((id) =>
        data.find((item) => item[uniqueKey] === id)
      );
      onSubmit(selectedData as T[]);
    }
  }, [onSubmit, selectedRows, data]);

  const tableStyle = useMemo(
    () =>
      classNames(styles.dataTable, {
        [styles.actionButtonTable]: columnActionButtons
      }),
    [columnActionButtons]
  );

  const rowStyle = useMemo(() => {
    return onRowClick || multipleRowSubmission ? styles.enableHover : '';
  }, [onRowClick, multipleRowSubmission]);

  const stickyStyle = useCallback(
    (colIndex: number, rowIndex: number) => {
      const cellBgColor =
        rowIndex % 2 === 1 ? styles.greyCellBackground : styles.whiteCellBackground;
      return !(
        colIndex === 0 &&
        stickyFirstColumn &&
        !multipleRowSubmission &&
        !columnActionButtons
      )
        ? ''
        : classNames(styles.sticky, cellBgColor);
    },
    [stickyFirstColumn, multipleRowSubmission]
  );

  const alignmentStyle = useCallback(
    (index: number) => styles[`align${columnSettings[index].rowAlignment}` as keyof typeof styles],
    [columnSettings]
  );

  const actionsColumnWrapperStyle = useCallback(
    (index: number) => {
      const cellBgColor = index % 2 === 1 ? styles.greyCellBackground : styles.whiteCellBackground;
      return classNames(styles.checkboxWidth, cellBgColor, { [styles.sticky]: stickyFirstColumn });
    },
    [stickyFirstColumn]
  );

  const handleBodyCellKeyDown = useEnterOrSpaceKey(handleMultipleElementClicks(['a', 'button']));
  const handleHeaderCellKeyDown = (columnKey: keyof T) =>
    useEnterOrSpaceKey(() => onSort(columnKey));

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableElement>) => {
      if (showRowActions) {
        // Prevent default DataTable arrow key behavior when ActionButtonDropdown is open
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.stopPropagation();
          return;
        }
      }

      if (!focusedCell) return;

      const { row, col } = focusedCell;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          if (col < columnSettings.length - 1) {
            setFocusedCell({ row, col: col + 1 });
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (col > 0) {
            setFocusedCell({ row, col: col - 1 });
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (row < data.length - 1) {
            setFocusedCell({ row: row + 1, col });
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (row > 0) {
            setFocusedCell({ row: row - 1, col });
          }
          break;
        default:
          break;
      }
    },
    [focusedCell, columnSettings.length, data.length, showRowActions]
  );

  const handleCellFocus = useCallback((rowIndex: number, colIndex: number) => {
    setFocusedCell({ row: rowIndex, col: colIndex });
  }, []);

  useHotkeys('ctrl+alt+t', () => {
    if (tableRef.current) {
      tableRef.current.focus();
      setFocusedCell({ row: 0, col: 0 });
    }
  });

  useHotkeys('ctrl+shift+alt+t', () => {
    setFocusedCell(null);
    if (document.activeElement) (document.activeElement as HTMLElement).blur();
  });

  useEffect(() => {
    if (focusedCell && tableRef.current) {
      const { row, col } = focusedCell;
      const colIncrement = multipleRowSubmission || columnActionButtons ? 2 : 1;
      const cell = tableRef.current.querySelector(
        `tbody tr:nth-child(${row + 1}) td:nth-child(${col + colIncrement})`
      ) as HTMLElement;
      if (cell) {
        cell.focus();
      }
    }
  }, [focusedCell]);

  const handleActionButtonClick = useCallback(
    (e: MouseOrKeyboardEvent, item: T, rowIndex: number) => {
      e.stopPropagation();
      actionButtonRef.current = e.currentTarget as HTMLButtonElement;

      if (rowIndex === openActionRowIndex) {
        setOpenActionRowIndex(null);
        setShowRowActions(false);
      } else {
        setOpenActionRowIndex(rowIndex);
        setShowRowActions(true);
      }

      setActionAnchor(e.currentTarget as HTMLElement);
      if (onRowSelectionChange) {
        onRowSelectionChange([item]);
      }
    },
    [onRowSelectionChange, openActionRowIndex]
  );

  const handleScrollOrResize = useCallback(() => {
    const LEFT_OFFSET = 4; // Same width as shadow overlay <div>
    if (!tableContainerRef.current || !tableRef.current || !shadowOverlayRef.current) return;

    const scrollEl = tableContainerRef.current;
    const shadowEl = shadowOverlayRef.current;
    let { scrollLeft } = scrollEl;

    if (Math.abs(scrollLeft) < 1) {
      scrollLeft = 0;
    }

    const firstStickyTh = tableRef.current.querySelector('thead tr th:first-child') as HTMLElement;
    if (!firstStickyTh) return;

    const stickyRect = firstStickyTh.getBoundingClientRect();
    const containerRect = scrollEl.getBoundingClientRect();

    const leftPos = Math.round(stickyRect.right - containerRect.left + scrollLeft - LEFT_OFFSET);
    const topPos = Math.round(tableRef.current.getBoundingClientRect().top - containerRect.top);
    const heightVal = Math.round(tableRef.current.getBoundingClientRect().height);
    if (overlayLeftPosRef.current === leftPos && scrollLeft === 0) return;
    overlayLeftPosRef.current = leftPos;

    requestAnimationFrame(() => {
      if (scrollLeft === 0) {
        shadowEl.style.opacity = '0';
      } else {
        shadowEl.style.opacity = '1';
        shadowEl.style.top = `${topPos}px`;
        shadowEl.style.height = `${heightVal}px`;
      }
      shadowEl.style.transform = `translateX(${leftPos}px)`;
    });
  }, []);

  useEffect(() => {
    const scrollEl = tableContainerRef.current;
    if (!scrollEl) return;

    scrollEl.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);
    handleScrollOrResize();

    return () => {
      scrollEl.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [handleScrollOrResize]);

  return (
    <div className={styles.dataTableMainContainer}>
      {!!actionButtons && actionButtons.length > 0 && (
        <ActionButtonDropdown
          anchorEl={actionAnchor}
          open={showRowActions}
          onToggle={setShowRowActions}
          options={actionButtons}
        />
      )}
      <div className={styles.tableContainer} ref={tableContainerRef}>
        <table
          ref={tableRef}
          className={tableStyle}
          tabIndex={-1}
          role="grid"
          aria-rowcount={data.length}
          aria-colcount={columnSettings.length}
          aria-invalid={errorType ? 'true' : 'false'}
          aria-errormessage="error-datatable"
          data-testid="data-table"
          onKeyDown={handleKeyDown}
        >
          <caption className={styles.caption}>{tableCaption}</caption>
          <thead>
            <tr role="row">
              {multipleRowSubmission && (
                <th
                  role="columnheader"
                  aria-sort="none"
                  data-testid="table-header-cell"
                  className={actionsColumnWrapperStyle(0)}
                >
                  <div className={styles.actionsCell}>
                    <Checkbox
                      indeterminate={isHeaderCheckboxIndeterminate}
                      checked={isHeaderCheckboxChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                  </div>
                </th>
              )}
              {columnActionButtons && (
                <th
                  role="columnheader"
                  aria-sort="none"
                  data-testid="table-header-cell"
                  className={actionsColumnWrapperStyle(0)}
                >
                  <div className={styles.actionsCell}>
                    <HbtIcon size={HbtIconSize.SMALL} type="icon_more_vertical" />
                  </div>
                </th>
              )}
              {columnSettings.map((column, index) => (
                <th
                  key={String(column.key)}
                  className={stickyStyle(index, 0)}
                  tabIndex={0}
                  role="columnheader"
                  aria-sort={
                    sortedColumn === column.key
                      ? sortAscending
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  data-testid="table-header-cell"
                  style={{ width: column.width }}
                  onKeyDown={handleHeaderCellKeyDown(column.key)}
                >
                  <DataTableHeader
                    label={column.displayName}
                    alignment={column.headerAlignment}
                    sortable={column.sortable}
                    isSorting={sortedColumn === column.key}
                    sortAscending={sortAscending}
                    leadingIcon={column.leadingIcon}
                    trailingIcon={column.trailingIcon}
                    sortIconPosition={column.sortIconPosition}
                    onSort={() => onSort(column.key)}
                  />
                  {columnResize && (
                    <div
                      className={styles.resizeHandle}
                      onMouseDown={(e) => handleMouseDown(e, index)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <tr key={index} role="row">
                  <td
                    role="gridcell"
                    colSpan={columnSettings.length + (multipleRowSubmission ? 1 : 0)}
                  >
                    <div className={styles.loadingBar}></div>
                  </td>
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr role="row">
                <td
                  role="gridcell"
                  colSpan={columnSettings.length + (multipleRowSubmission ? 1 : 0)}
                >
                  <div className={styles.noDataMessage}>
                    {emptyTableMessage ??
                      i18n.t('Globals-Table-No-Search-To-Display', 'No result to display')}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, rowIndex) => (
                <tr key={rowIndex} role="row" className={rowStyle}>
                  {multipleRowSubmission && (
                    <td role="gridcell" className={actionsColumnWrapperStyle(rowIndex)}>
                      <div className={styles.actionsCell}>
                        <Checkbox
                          errorCheckbox={!!errorType}
                          checked={selectedRows.has(item[uniqueKey] as unknown as string)}
                          onChange={() => handleCheckboxChange(item)}
                        />
                      </div>
                    </td>
                  )}
                  {columnActionButtons && (
                    <td role="gridcell" className={actionsColumnWrapperStyle(rowIndex)}>
                      <div className={styles.actionsCell}>
                        <Button
                          name="icon_more_vertical"
                          text={{ value: 'Row Actions' }}
                          ariaText={{ value: 'Row Actions' }}
                          className={classNames(styles.framelessButton, {
                            [styles.borderedActionRowButton]:
                              showRowActions && rowIndex === openActionRowIndex
                          })}
                          size={ButtonSize.SMALL}
                          variant={ButtonVariant.FRAMELESSICON}
                          icon={() => (
                            <HbtIcon size={HbtIconSize.SMALL} type="icon_more_vertical" />
                          )}
                          onClick={(e: MouseOrKeyboardEvent) =>
                            handleActionButtonClick(e, item, rowIndex)
                          }
                        />
                      </div>
                    </td>
                  )}
                  {columnSettings.map((column, colIndex) => (
                    <td
                      key={String(column.key)}
                      tabIndex={0}
                      role="gridcell"
                      className={stickyStyle(colIndex, rowIndex)}
                      style={{ width: column.width }}
                      onClick={() => handleRowClick(item)}
                      onFocus={() => handleCellFocus(rowIndex, colIndex)}
                      onKeyDown={handleBodyCellKeyDown}
                    >
                      <div className={alignmentStyle(colIndex)}>
                        {column.render
                          ? column.render(item[column.key], item)
                          : String(item[column.key])}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {tableErrorMessage && (
          <Alert className={styles.alertBox} severity="error" id="error-datatable" role="alert">
            {tableErrorMessage}
          </Alert>
        )}
        <div>{footer && footer}</div>
        {stickyFirstColumn && <div className={styles.shadowOverlay} ref={shadowOverlayRef} />}
      </div>
      {multipleRowSubmission && data.length > 0 && (
        <div className={styles.dataTableSubmitButtonContainer}>
          {deleteItem && (
            <Button
              name="table-delete-item"
              customStyles={submitButtonStyles}
              ariaText={{ value: deleteButtonLabel }}
              text={{ value: deleteButtonLabel }}
              loading={isDeleteButtonLoading}
              disabled={isDeleteButtonLoading}
              variant={ButtonVariant.SECONDARY}
              onClick={(e?: MouseOrKeyboardEvent) => handleDelete(e)}
            />
          )}
          <Button
            name="table-submit"
            customStyles={submitButtonStyles}
            ariaText={{ value: submitButtonLabel }}
            text={{ value: submitButtonLabel }}
            loading={isSubmitButtonLoading}
            disabled={isSubmitButtonLoading}
            variant={ButtonVariant.PRIMARY}
            onClick={handleSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default memo(DataTable) as typeof DataTable;
